import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { apiClient, useAuth } from "@/lib/api";
import { useGuidance } from "@/lib/GuidanceContext";
import { useProfile } from "@/lib/ProfileContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ExpandablePostCard from "@/components/ExpandablePostCard";
import ShareModal from "@/components/ShareModal";
import { 
  Search, 
  Edit, 
  Calendar,
  Clock,
  CheckCircle,
  Share2,
  Gift,
  FileText,
  Video,
  Inbox,
  Image,
  Archive,
  Sparkles,
  Loader2,
  Zap
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

// Removed "To Schedule" - all content auto-schedules
const tabs = [
  { id: "scheduled", label: "Scheduled", icon: Clock, color: "text-blue-500" },
  { id: "approved", label: "Approved", icon: CheckCircle, color: "text-green-500", note: "Ready to post" },
  { id: "published", label: "Posted", icon: CheckCircle, color: "text-emerald-500" },
  { id: "archived", label: "Archived", icon: Archive, color: "text-gray-500", note: "Auto-deletes in 30 days" }
];

export default function PostsPage() {
  const { user } = useAuth();
  const { glowTargets, markPostApproved, markScheduledViewed } = useGuidance();
  const { activeProfile } = useProfile();
  const [posts, setPosts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const highlightPostId = location.state?.highlightPostId || searchParams.get("highlight");
  const initialTab = searchParams.get("tab") || "scheduled";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [search, setSearch] = useState("");
  const [editingPost, setEditingPost] = useState(null);
  const [editedCaption, setEditedCaption] = useState("");
  const [editedHashtags, setEditedHashtags] = useState("");
  const [highlightedPostId, setHighlightedPostId] = useState(highlightPostId);
  const [aiAssisting, setAiAssisting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const navigate = useNavigate();
  const postRefs = useRef({});
  
  // Scroll to highlighted post when posts load
  useEffect(() => {
    if (highlightedPostId && posts.length > 0) {
      const timer = setTimeout(() => {
        const element = postRefs.current[highlightedPostId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a visual pulse effect
          element.classList.add('ring-2', 'ring-viraly-orange', 'ring-offset-2', 'ring-offset-viraly-dark');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-viraly-orange', 'ring-offset-2', 'ring-offset-viraly-dark');
          }, 3000);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [highlightedPostId, posts]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const POSTS_PER_PAGE = 10;

  // Count posts by status - include ALL content types (posts + videos)
  // Using useMemo to ensure counts update when posts change
  // Note: "pending" status is included in scheduled tab (posts awaiting approval)
  const counts = useMemo(() => ({
    scheduled: posts.filter(p => p.status === "scheduled" || p.status === "draft" || p.status === "pending").length,
    approved: posts.filter(p => p.status === "approved").length,
    published: posts.filter(p => p.status === "published").length,
    archived: posts.filter(p => p.status === "archived").length
  }), [posts]);

  // Scroll to and highlight the specific post when coming from Dashboard
  useEffect(() => {
    if (highlightPostId && posts.length > 0) {
      // Find which tab the post is in
      const post = posts.find(p => p.id === highlightPostId);
      if (post) {
        // Switch to the correct tab
        if (post.type === "video" || post.video_url) {
          setActiveTab("video");
        } else if (post.status === "published") {
          setActiveTab("published");
        } else {
          setActiveTab("scheduled");
        }
        
        // Scroll to post after a brief delay for tab switch
        setTimeout(() => {
          const postElement = postRefs.current[highlightPostId];
          if (postElement) {
            postElement.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 300);
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          setHighlightedPostId(null);
          // Clear location state
          navigate(location.pathname, { replace: true, state: {} });
        }, 3000);
      }
    }
  }, [highlightPostId, posts, navigate, location.pathname]);

  const fetchPosts = async () => {
    try {
      const profileParam = activeProfile?.id ? `?profile_id=${activeProfile.id}` : '';
      const res = await apiClient.get(`/posts${profileParam}`);
      setPosts(res.data);
    } catch (error) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  // Refetch when profile changes
  useEffect(() => {
    fetchPosts();
  }, [activeProfile?.id]);

  // Filter posts based on active tab and sort by CREATED date (newest first)
  const getFilteredPosts = () => {
    let filtered = posts;
    
    // Apply search filter (search in caption, hashtags, platform)
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(post => 
        post.caption?.toLowerCase().includes(searchLower) ||
        post.title?.toLowerCase().includes(searchLower) ||
        post.hashtags?.some(h => h.toLowerCase().includes(searchLower)) ||
        post.platform?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply tab filter - videos now show alongside regular posts
    if (activeTab === "scheduled") {
      // Include scheduled, draft, and pending (all content awaiting approval)
      filtered = filtered.filter(p => p.status === "scheduled" || p.status === "draft" || p.status === "pending");
    } else if (activeTab === "archived") {
      // Show only archived posts
      filtered = filtered.filter(p => p.status === "archived");
    } else {
      filtered = filtered.filter(p => p.status === activeTab);
    }
    
    // Sort by CREATED date - newest first (most recently created on top)
    // For archived, sort by archived_at date
    return filtered.sort((a, b) => {
      const dateA = new Date(activeTab === "archived" ? (a.archived_at || a.created_at || 0) : (a.created_at || 0));
      const dateB = new Date(activeTab === "archived" ? (b.archived_at || b.created_at || 0) : (b.created_at || 0));
      return dateB - dateA; // Newest first
    });
  };
  
  // Get paginated posts
  const filteredPosts = getFilteredPosts();
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const paginatedPosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );
  
  // Reset to page 1 when tab or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, search]);

  const handleDelete = async (postId) => {
    if (!confirm("Are you sure you want to delete this post?")) return;
    
    try {
      await apiClient.delete(`/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setEditedCaption(post.caption);
    // Convert hashtags array to string for editing
    setEditedHashtags(Array.isArray(post.hashtags) ? post.hashtags.join(" ") : post.hashtags || "");
  };

  const handleSaveEdit = async () => {
    try {
      // Parse hashtags from string - handle both space and comma separated
      const hashtagsArray = editedHashtags
        .split(/[\s,]+/)
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0)
        .map(tag => tag.startsWith("#") ? tag : `#${tag}`);
      
      const updateData = { 
        caption: editedCaption,
        hashtags: hashtagsArray
      };
      
      // Also save viral score if it was updated by AI Assist
      if (editingPost?.viral_score) {
        updateData.viral_score = editingPost.viral_score;
      }
      
      await apiClient.put(`/posts/${editingPost.id}`, updateData);
      setPosts(posts.map(p => p.id === editingPost.id ? { 
        ...p, 
        caption: editedCaption, 
        hashtags: hashtagsArray,
        viral_score: editingPost.viral_score || p.viral_score
      } : p));
      setEditingPost(null);
      toast.success("Post updated");
    } catch (error) {
      toast.error("Failed to update post");
    }
  };

  // AI Assist - Improve caption for better viral score
  const handleAiAssist = async () => {
    if (!editedCaption.trim()) {
      toast.error("Please enter a caption first");
      return;
    }
    
    setAiAssisting(true);
    try {
      const res = await apiClient.post("/content/improve-caption", {
        caption: editedCaption,
        platform: editingPost?.platform || "instagram"
      });
      
      if (res.data.improved_caption) {
        setEditedCaption(res.data.improved_caption);
        
        // Update viral score in the editing post
        if (res.data.viral_score) {
          setEditingPost(prev => ({ ...prev, viral_score: res.data.viral_score }));
        }
        
        const scoreMsg = res.data.viral_score ? ` New viral score: ${res.data.viral_score}%` : '';
        toast.success(`Caption improved!${scoreMsg} Review and save.`);
      }
    } catch (error) {
      console.error("AI Assist error:", error);
      toast.error("Failed to improve caption. Please try again.");
    } finally {
      setAiAssisting(false);
    }
  };

  const handleRegenerate = async (postId) => {
    try {
      const res = await apiClient.post(`/posts/${postId}/regenerate`);
      // New behavior: All related posts (same content, different platforms) are archived
      // New posts created for all platforms
      setPosts(prevPosts => {
        // Mark all archived posts as archived in local state
        const archivedIds = res.data.archived_post_ids || [postId];
        const updated = prevPosts.map(p => 
          archivedIds.includes(p.id) ? { ...p, status: 'archived', archived_at: new Date().toISOString() } : p
        );
        // Add all new regenerated posts at the beginning
        const newPosts = res.data.posts || (res.data.post ? [res.data.post] : []);
        return [...newPosts, ...updated];
      });
      
      const platformCount = res.data.platforms_regenerated?.length || 1;
      toast.success(res.data.message || `Regenerated for ${platformCount} platform(s)! Used ${res.data.credits_used} regen credit(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to regenerate");
    }
  };

  // Handle post update (e.g., when approved/rejected)
  const handleUpdatePost = (updatedPost) => {
    setPosts(posts.map(p => p.id === updatedPost.id ? updatedPost : p));
  };

  const handleShare = async () => {
    // On mobile with native share support, use it
    if (navigator.share && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      try {
        await navigator.share({
          title: 'Viraly - AI Social Media Automation',
          text: 'I\'m automating my social media with Viraly! Get 5 free posts to start:',
          url: window.location.origin
        });
      } catch (error) {
        // User cancelled
      }
    } else {
      // On desktop, show our custom share modal with referral info
      setShowShareModal(true);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-1">Your Posts</h1>
            <p className="text-viraly-muted">{posts.length} total posts</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost"
              onClick={handleShare}
              className="text-viraly-muted hover:text-white flex items-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share & Earn</span>
            </Button>
            {counts.scheduled > 0 && (
              <Button 
                onClick={() => navigate("/swipe-approve")}
                variant="outline"
                className="hidden sm:flex border-viraly-orange/50 text-viraly-orange hover:bg-viraly-orange/10"
                data-testid="swipe-approve-btn"
              >
                Swipe Approve ({counts.scheduled})
              </Button>
            )}
            <Button 
              onClick={() => navigate("/create")}
              className="btn-primary"
              data-testid="create-post-btn"
            >
              Create New Post
            </Button>
          </div>
        </div>

        {/* Points Banner - Clickable */}
        <button 
          onClick={handleShare}
          className="glass-card p-3 mb-4 flex items-center justify-center gap-2 text-sm w-full hover:bg-white/10 transition-colors cursor-pointer"
        >
          <Gift className="w-4 h-4 text-viraly-orange" />
          <span className="text-viraly-muted">Share Viraly with friends and</span>
          <span className="text-viraly-orange font-medium">earn points towards discounts!</span>
          <Share2 className="w-4 h-4 text-viraly-orange ml-2" />
        </button>

        {/* Consent Disclaimer */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 mb-6 flex items-start gap-3">
          <Calendar className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-300">
              <strong>Auto-Posting Notice:</strong> Posts in your scheduled queue will be automatically published at their scheduled time once you connect your social accounts.
            </p>
            <p className="text-xs text-viraly-muted mt-1">
              By scheduling content, you consent to automatic posting. Edit or delete posts anytime before publishing.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-2">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            const count = counts[tab.id] || 0;
            
            // Check if this tab should glow
            const shouldGlow = !!(
              (tab.id === "draft" && glowTargets?.reviewApprove) ||
              (tab.id === "scheduled" && glowTargets?.scheduled)
            );
            
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  // Mark scheduled as viewed to stop the glow
                  if (tab.id === "scheduled" && markScheduledViewed) {
                    markScheduledViewed();
                  }
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-viraly-orange text-white'
                    : 'bg-white/5 text-viraly-muted hover:bg-white/10'
                } ${shouldGlow && activeTab !== tab.id ? 'glow-guide' : ''}`}
                data-testid={`tab-${tab.id}`}
              >
                <TabIcon className={`w-4 h-4 ${activeTab === tab.id ? 'text-white' : tab.color}`} />
                {tab.label}
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-white/10'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        
        {/* Tab note for Approved */}
        {activeTab === "approved" && (
          <p className="text-xs text-viraly-muted mb-4 ml-1">
            ✓ These posts are approved and will auto-publish at their scheduled time
          </p>
        )}
        {activeTab === "archived" && (
          <p className="text-xs text-gray-500 mb-4 ml-1">
            📦 Archived posts are view-only and will be automatically deleted after 30 days
          </p>
        )}
        {activeTab !== "approved" && activeTab !== "archived" && <div className="mb-4" />}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
            <Input
              placeholder="Search by caption, #hashtag, or platform (facebook, instagram, tiktok, pinterest, linkedin, youtube, twitter)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-base pl-10"
              data-testid="search-posts"
            />
          </div>
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="card-base p-12 text-center">
            <Inbox className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
            <p className="text-viraly-muted mb-4">
              {activeTab === "draft" && "No posts waiting to be scheduled"}
              {activeTab === "scheduled" && "No scheduled posts yet"}
              {activeTab === "published" && "No published posts yet"}
              {activeTab === "archived" && "No archived posts"}
            </p>
            {activeTab !== "archived" && (
              <Button onClick={() => navigate("/create")} className="btn-primary">
                Create Post
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {paginatedPosts.map((post, i) => (
              <div 
                key={post.id}
                ref={(el) => postRefs.current[post.id] = el}
                className={`transition-all duration-500 rounded-xl ${
                  highlightedPostId === post.id 
                    ? 'ring-2 ring-viraly-orange ring-offset-2 ring-offset-viraly-dark' 
                    : ''
                } ${post.status === 'archived' ? 'opacity-50 grayscale' : ''}`}
              >
                <ExpandablePostCard
                  post={post}
                  index={i}
                  showActions={post.status !== 'archived'}
                  onEdit={post.status !== 'archived' ? handleEdit : undefined}
                  onDelete={post.status !== 'archived' ? (p) => handleDelete(p.id) : undefined}
                  onSchedule={post.status !== 'archived' ? () => navigate('/calendar') : undefined}
                  onRegenerate={post.status !== 'archived' ? handleRegenerate : undefined}
                  onUpdatePost={handleUpdatePost}
                  isHighlighted={highlightedPostId === post.id}
                  isArchived={post.status === 'archived'}
                />
              </div>
            ))}
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-white/10">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary px-3 py-2"
                  data-testid="pagination-prev"
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === pageNum
                            ? 'bg-viraly-orange text-white'
                            : 'bg-white/5 hover:bg-white/10 text-viraly-muted'
                        }`}
                        data-testid={`pagination-page-${pageNum}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="btn-secondary px-3 py-2"
                  data-testid="pagination-next"
                >
                  Next
                </Button>
                
                <span className="text-xs text-viraly-muted ml-2">
                  Page {currentPage} of {totalPages} ({filteredPosts.length} total)
                </span>
              </div>
            )}
          </div>
        )}

        {/* Edit Dialog with AI Assist */}
        <Dialog open={!!editingPost} onOpenChange={() => setEditingPost(null)}>
          <DialogContent className="bg-viraly-card border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Edit Post Caption</span>
                {editingPost?.viral_score && (
                  <div className="flex items-center gap-2 text-sm">
                    <Zap className="w-4 h-4 text-viraly-orange" />
                    <span className="text-viraly-muted">Viral Score:</span>
                    <span className={`font-bold ${
                      editingPost.viral_score >= 80 ? 'text-green-400' :
                      editingPost.viral_score >= 60 ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>
                      {editingPost.viral_score}%
                    </span>
                  </div>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {/* Platform indicator */}
            {editingPost?.platform && (
              <div className="flex items-center gap-2 text-xs text-viraly-muted mb-2">
                <span className="capitalize">{editingPost.platform}</span>
                <span>•</span>
                <span>{editedCaption.length} characters</span>
              </div>
            )}
            
            <Textarea
              value={editedCaption}
              onChange={(e) => setEditedCaption(e.target.value)}
              className="input-base min-h-[150px]"
              placeholder="Write your caption..."
              data-testid="edit-caption-textarea"
            />
            
            {/* Hashtags Editor with AI Assist */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-viraly-muted">Hashtags (space or comma separated)</label>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setAiAssisting(true);
                      const response = await apiClient.post('/content/ai-assist/hashtags', {
                        caption: editedCaption,
                        platform: editingPost?.platform || 'instagram',
                        count: 15
                      });
                      if (response.data.success && response.data.combined_string) {
                        setEditedHashtags(response.data.combined_string);
                        toast.success('AI hashtags generated!');
                      }
                    } catch (err) {
                      console.error('Hashtag generation failed:', err);
                      toast.error('Failed to generate hashtags');
                    } finally {
                      setAiAssisting(false);
                    }
                  }}
                  disabled={aiAssisting}
                  className="text-xs text-viraly-orange hover:underline flex items-center gap-1"
                  data-testid="ai-hashtags-btn"
                >
                  <Sparkles className="w-3 h-3" />
                  AI Hashtags
                </button>
              </div>
              <Input
                value={editedHashtags}
                onChange={(e) => setEditedHashtags(e.target.value)}
                className="input-base"
                placeholder="#viraly #marketing #socialmedia"
                data-testid="edit-hashtags-input"
              />
              <p className="text-xs text-viraly-muted mt-1">
                {editedHashtags.split(/[\s,]+/).filter(t => t.trim()).length} hashtags
              </p>
            </div>
            
            {/* AI Assist Button */}
            <Button
              variant="outline"
              onClick={handleAiAssist}
              disabled={aiAssisting || !editedCaption.trim()}
              className="w-full border-viraly-orange/50 text-viraly-orange hover:bg-viraly-orange/10 flex items-center justify-center gap-2"
              data-testid="ai-assist-btn"
            >
              {aiAssisting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Improving caption...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  AI Assist - Improve Viral Score
                </>
              )}
            </Button>
            <p className="text-xs text-viraly-muted text-center -mt-2">
              Let AI optimize your caption for better engagement
            </p>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setEditingPost(null)} className="btn-secondary">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} className="btn-primary" data-testid="save-edit-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Share Modal */}
        <ShareModal 
          isOpen={showShareModal} 
          onClose={() => setShowShareModal(false)}
          shareData={{
            title: "Viraly - AI Social Media Automation",
            text: user?.referral_code 
              ? `I'm automating my social media with Viraly! Use my code ${user.referral_code} for a discount!`
              : "I'm automating my social media with Viraly! Check it out!",
            url: user?.referral_code 
              ? `${window.location.origin}?ref=${user.referral_code}`
              : window.location.origin
          }}
        />
      </div>
    </DashboardLayout>
  );
}
